import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';

import { updateShowTermsStatus } from '../../actions/home';
import TermModal from '../../components/Modal/TermModal';
import NotFound from '../../components/NotFound';
import {
  checkAuthenticationAdmin,
  checkAuthenticationSuperAdmin,
  checkAuthenticationUser,
} from '../../helpers/lib';
import useSSE from '../../helpers/sse';
import { setStorage, STORAGE_KEY } from '../../helpers/storage';
import useTerms from '../../hooks/useTerms';
import AdminLayout from '../../layouts/AdminLayout';
import PrivateLayout from '../../layouts/PrivateLayout';
import PrivateUserLayout from '../../layouts/PrivateUserLayout';
import {
  adminRouters,
  privateRouters,
  privateUserRouters,
  superAdminRouters,
  userRouters,
} from '../../router';
import { urlSSE } from '../../services/configs/axiosConfig';
import { User } from '../../types';

// Component to handle unauthorized access to admin routes
const UnauthorizedRedirect = () => {
  const history = useHistory();

  useEffect(() => {
    console.log('🔍 Unauthorized access to admin route, redirecting to home...');
    history.replace('/');
  }, [history]);

  return null;
};

interface AppContentProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
}

const routeRender = (route: any) => {
  const Component = route.container;
  return <Route key={route.path} exact={route.exact} path={route.path} component={Component} />;
};

export const AppContent = ({
  isAuthenticated,
  isLoading,
  user,
  login,
  logout: _logout,
}: AppContentProps) => {
  const dispatch = useDispatch();
  const isShowingTerms = useSelector((state) => state.home.isShowingTerms);

  const authUser = checkAuthenticationUser();
  const authAdmin = checkAuthenticationAdmin();
  const authSuperAdmin = checkAuthenticationSuperAdmin();
  const shouldRenderPrivate = !authUser && (authAdmin || authSuperAdmin);
  const [hasValidToken, setHasValidToken] = useState(false);

  // Use hook to get Terms URL from API (but don't use auto-show logic)
  const { termsUrl } = useTerms({ tokenAvailable: hasValidToken });

  const onOpen = (_item: any) => {
    console.log('🔗 SSE Connection opened:', _item);
  };
  const onMessage = (_item: any) => {
    console.log('📨 SSE Message received:', _item);
  };
  const onError = (_item: any) => {
    console.log('❌ SSE Error:', _item);
  };

  useEffect(() => {
    if (user?.token) {
      console.log('💾 Saving new token to localStorage:', user.token.substring(0, 50) + '...');
      setStorage(STORAGE_KEY.TOKEN, user.token);
      setHasValidToken(true);
    }
    if (user?.tokenParsed?.locale) {
      setStorage(STORAGE_KEY.I18_NEXT_LANGUAGE, user.tokenParsed.locale);
    }
  }, [user]);
  useSSE({ uri: urlSSE, onOpen, onMessage, onError, tokenAvailable: hasValidToken });

  const onClose = () => {
    dispatch(updateShowTermsStatus(false));
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <h2>Please login to continue</h2>
        <button
          onClick={login}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Login with Keycloak
        </button>
      </div>
    );
  }

  return (
    <>
      <TermModal
        isOpen={isShowingTerms}
        termUrl={termsUrl}
        showActions={false}
        onAccept={() => onClose()}
        onDecline={() => dispatch(updateShowTermsStatus(false))}
      />
      <Switch>
        {/* Private routes for user */}
        {checkAuthenticationUser() && (
          <Route exact path={privateUserRouters.map((route) => route.path)}>
            <PrivateUserLayout isLoading={isLoading}>
              <Switch>{privateUserRouters.map(routeRender)}</Switch>
            </PrivateUserLayout>
          </Route>
        )}

        {/* Private routes for admin and super admin */}
        {shouldRenderPrivate &&
          privateRouters.map((route) => (
            <Route key={route.key} exact={route.exact} path={route.path}>
              <PrivateLayout isLoading={isLoading}>
                <Switch>{privateRouters.map(routeRender)}</Switch>
              </PrivateLayout>
            </Route>
          ))}

        {/* Admin routes - accessible by both admin and super admin */}
        {(checkAuthenticationAdmin() || checkAuthenticationSuperAdmin()) && (
          <Route exact path={adminRouters.map((route) => route.path)}>
            <AdminLayout isLoading={isLoading}>
              <Switch>{adminRouters.map(routeRender)}</Switch>
            </AdminLayout>
          </Route>
        )}

        {/* Super admin routes - only accessible by super admin */}
        {checkAuthenticationSuperAdmin() && (
          <Route exact path={superAdminRouters.map((route) => route.path)}>
            <AdminLayout isLoading={isLoading}>
              <Switch>{superAdminRouters.map(routeRender)}</Switch>
            </AdminLayout>
          </Route>
        )}

        {/* User routes */}
        {checkAuthenticationUser() && (
          <Route exact path={userRouters.map((route) => route.path)}>
            <PrivateUserLayout isLoading={isLoading}>
              <Switch>{userRouters.map(routeRender)}</Switch>
            </PrivateUserLayout>
          </Route>
        )}

        {/* Redirect unauthorized access to admin routes back to home */}
        <Route path='/management-system'>
          <UnauthorizedRedirect />
        </Route>
        <Route path='/sync-sharepoint'>
          <UnauthorizedRedirect />
        </Route>
        <Route path='/synchronization'>
          <UnauthorizedRedirect />
        </Route>
        <Route path='/sync-projects'>
          <UnauthorizedRedirect />
        </Route>
        <Route path='/sync-documents'>
          <UnauthorizedRedirect />
        </Route>
        <Route path='/admin/users'>
          <UnauthorizedRedirect />
        </Route>

        <Route path='*' component={NotFound} />
      </Switch>
    </>
  );
};
